import { Dispatch, SetStateAction, useState } from "react";
import { useWatch } from "react-hook-form";
import classNames from "classnames";
import { gql, useQuery, useMutation } from "@apollo/client";
import { client } from "../../../contexts/ApolloProvider";

import Button from "../../Button";
import { Text } from "../../Type";
import styles from "./index.module.scss";
import TextLink from "../../TextLinks";
import NotificationBanner from "../../../components/NotificationBanner";
import ModalActions from "../../ModalActions";
import FileContainer from "./FileContainer";

const GET_CLIENT_FILE = gql`
  query GetClientFile($id: ID!) {
    clientFile(id: $id) {
      id
      wet_signature {
        name
        url
      }
    }
  }
`;

const INITIATE_VENDOR_SIGNING = gql`
  mutation VendorSignClientFile($input: SignClientFileInput) {
    vendorSignClientFile(input: $input) {
      url
    }
  }
`;

type PropsType = {
  clientFileId: string | undefined;
  clientFormId: string | undefined;
  setSkipModalOpen: Dispatch<SetStateAction<boolean>>;
};

const SkipSignatureModal = ({ clientFileId, clientFormId, setSkipModalOpen }: PropsType) => {
  const [hasFile, setHasFile] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const assessmentDate = useWatch({ name: "section4.authorizer-assessment-date", });
  const { data, loading, error } = useQuery(GET_CLIENT_FILE, {
    fetchPolicy: "network-only",
    variables: {
      id: clientFileId,
    },
    onCompleted: () => {
      setHasFile(data.clientFile.wet_signature);
    },
    onError: (err) => {
      console.error("SkipSignatureModal: failed to load client file", err);
      setQueryError("Failed to load file details. Please close and try again.");
    },
  });

  const [initiateVendorSigning, { error: errorSkip, loading: loadingSkip }] = useMutation(INITIATE_VENDOR_SIGNING,{
    onCompleted: (data) => {
      window.location.href = data.vendorSignClientFile.url;
      setSkipModalOpen(false);
    },
    onError: (err) => {
      console.error("SkipSignatureModal: vendorSignClientFile failed", err);
      setMutationError("Submission failed. Please try again later.");
    },
  }
  );

  if (loading) return <p>Loading...</p>;
  if (error || queryError) return (
    <NotificationBanner
      error
      title="Error"
      content={queryError ?? "Failed to load file details. Please close and try again."}
      className={styles.marginBanner}
    />
  );

  const handleSubmit = async () => {
    const apolloClient = client;
    const response = await apolloClient.query({
      query: gql`
        query ClientForm($id: ID!) {
          clientForm(id: $id) {
            id
            form_data
          }
        }
      `,
      variables: {
        id: clientFormId,
      },
      fetchPolicy: "no-cache",
    });

    const responseAssessmentDate = response.data.clientForm?.form_data?.section4?.["authorizer-assessment-date"];

    if (responseAssessmentDate === assessmentDate) {
      initiateVendorSigning({
        variables: {
          input: {
            formId: clientFormId,
          },
        },
      });
    } else {
      setTimeout(() => {
        initiateVendorSigning({
          variables: {
            input: {
              formId: clientFormId,
            },
          },
        });
      }, 1500);
    }
  };

  return clientFileId ? (
    <>
      {!hasFile &&
        <NotificationBanner
          warning
          title="Missing Scanned Signature File"
          content="A scanned copy of the physical wet signature must be uploaded before submitting an application to the ADP portal."
          className={styles.marginBanner}
        />
      }
      {loadingSkip &&
        <NotificationBanner
          warning
          title="Please wait..."
          className={styles.marginBanner}
        />
      }
      {(errorSkip || mutationError) &&
        <NotificationBanner
          error
          title={mutationError ?? "Error while submitting. Please try later."}
          className={styles.marginBanner}
        />
      }
      <Text>
        You&#39;re about to skip e-signature and submit application for Funding Mobility Devices.
        It is an offence punishable by fine/or imprisonment to knowingly provide false information
        to obtain funding for a device.
      </Text>
      <br/>
      <div
        key="wet_signature"
        className={classNames(styles.modalGrid5050, styles.tableItems)}
      >
        <div>
          <Text size="medium" weight="bold">
            Wet Signature Document
          </Text>
        </div>
        <div>
          <FileContainer
            file={data.clientFile.wet_signature}
            fieldName="wet_signature"
            clientFileId={clientFileId}
            setHasFile={setHasFile}
            disabled={loadingSkip}
          />
        </div>
      </div>

      <ModalActions>
        <Button
          primary
          onClick={handleSubmit}
          disabled={loadingSkip}
        >
          I understand, submit without signing
        </Button>
        <TextLink
          onClick={() => setSkipModalOpen(false)}
          disabled={loadingSkip}
        >
          Return to form
        </TextLink>
      </ModalActions>
    </>
  ) : (
    <NotificationBanner
      error
      title="Error"
      content="Client file ID unknown"
      className={styles.marginBanner}
    />
  );
};

export default SkipSignatureModal;
